import { useEffect, useState } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack, useTheme, Typography, Divider } from '@mui/material';
import { UserWorkspaceProfile } from 'models/UserWorkspaceProfile';
import { DisplayNamesInput } from './DisplayNamesInput';
import { useSendMessages } from 'hooks/useSendMessages';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

interface SendMessageDialogProps {
  selectedUsers: UserWorkspaceProfile[];
  allUsers: UserWorkspaceProfile[];
  open: boolean;
  onClose: () => void;
  onSend: (subject: string, content: string) => void;
}

export const SendMessageDialog: React.FC<SendMessageDialogProps> = ({ selectedUsers, allUsers, open, onClose, onSend }) => {
  const [selectedDisplayNames, setSelectedDisplayNames] = useState<string[]>([]);
  const [allDisplayNames, setAllDisplayNames] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const { sendMessages, sendingMessages } = useSendMessages();
  const { t } = useTranslation();
  const theme = useTheme();
  
  const handleDisplayNamesChange = (displayNames: string[]) => {
    setSelectedDisplayNames(displayNames);
  };

  const handleSendClick = async () => {
    const recipientEmails = allUsers.filter(user => selectedDisplayNames.includes(user.displayName)).map(user => user.email);

    await sendMessages(recipientEmails, subject, content);
    setContent('');
    setSubject('');
    onClose();
  };

  useEffect (() => {
    if (selectedUsers) {
      setSelectedDisplayNames(selectedUsers.map(user => user.displayName));
    }
  },[selectedUsers]);

  useEffect(() => {
    if (allUsers) {
      setAllDisplayNames(allUsers.map(user => user.displayName));
    }
  }, [allUsers])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} paddingTop={theme.spacing(10)}>
          <Typography variant='h5' fontWeight={'500'}>{t('sendMessage.title')}</Typography>
          <Typography variant='subtitle1' color={theme.palette.text.secondary}>{t('sendMessage.subtitle')}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4} padding={theme.spacing(10)}>
          <DisplayNamesInput displayNames={selectedDisplayNames} options={allDisplayNames} onChange={handleDisplayNamesChange} />
          <Divider />
          <Typography variant='subtitle2' fontWeight={'500'}>{t('sendMessage.enterMessageContent')}</Typography>
          <TextField
            label={t('sendMessage.subject')}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('sendMessage.messageContent')}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            fullWidth
              margin="normal"
              multiline
              rows={3}
              InputProps={{
                style: { borderRadius: 12 }
              }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack paddingLeft={theme.spacing(10)} paddingRight={theme.spacing(10)} direction='row' spacing={theme.spacing(3)} marginBottom={theme.spacing(10)}>
          <Button onClick={onClose} variant='outlined'>{t('sendMessage.cancel')}</Button>
          <LoadingButton loading={sendingMessages} onClick={handleSendClick} variant="contained" color="primary">
            {t('sendMessage.sendMessage')}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};